import { useEffect } from 'react';

import { t } from '@web/i18n';
import {
  AppIcons,
  Button,
  Card,
  Label,
  PageSectionHead,
  Savebar,
  Switch,
  Textarea,
} from '@web/ui/core';
import { twcn } from '@web/ui/core/utils';

import { useTriageConfigPage } from '@web/ui/settings/hooks/use_triage_config_page';

type TriageController = ReturnType<typeof useTriageConfigPage>;

export default function TriageConfigPage(): React.JSX.Element | null {
  const ctrl = useTriageConfigPage();

  // ⌘S / Ctrl+S to save when dirty, Esc to discard. DOM subscription is
  // the canonical use case for useEffect per apps/clients/web/AGENTS.md.
  useEffect(() => {
    if (!ctrl.isReady) return undefined;
    function onKey(e: KeyboardEvent): void {
      if ((e.metaKey || e.ctrlKey) && e.key === 's' && ctrl.isDirty) {
        e.preventDefault();
        ctrl.save();
      } else if (e.key === 'Escape' && ctrl.isDirty) {
        ctrl.discard();
      }
    }
    globalThis.addEventListener('keydown', onKey);
    return () => {
      globalThis.removeEventListener('keydown', onKey);
    };
  }, [ctrl]);

  if (!ctrl.isReady) return null;
  return <TriageEditor ctrl={ctrl} />;
}

function TriageEditor({ ctrl }: Readonly<{ ctrl: TriageController }>): React.JSX.Element {
  const editorDisabled = !ctrl.enabled;

  return (
    <>
      <PageSectionHead title={t('triage.title')} sub={t('triage.sub')} />

      <Card>
        <div className="p-5">
          <header className="mb-5 flex flex-wrap items-start gap-4">
            <div className="min-w-0 flex-1">
              <h2 className="m-0 text-meta font-medium text-foreground">
                {t('triage.rules.title')}
              </h2>
              <p className="m-0 mt-1 text-meta text-muted-foreground">{t('triage.rules.sub')}</p>
            </div>
            <div className="flex items-start gap-3">
              <div className="flex flex-col items-end text-right">
                <Label
                  htmlFor="triage-enabled"
                  className="text-xs font-medium leading-4 tracking-letter-space text-foreground"
                >
                  {t('triage.enabled.label')}
                </Label>
                <span className="mt-0.5 max-w-prose-sm text-micro leading-normal text-muted-foreground">
                  {t('triage.enabled.hint')}
                </span>
              </div>
              <Switch
                id="triage-enabled"
                checked={ctrl.enabled}
                onCheckedChange={ctrl.setEnabled}
                aria-label={t('triage.enabled.label')}
              />
            </div>
          </header>

          <div
            className={twcn(
              'grid grid-cols-1 gap-5 lg:grid-cols-3',
              editorDisabled && 'opacity-50',
            )}
          >
            <PromptEditor ctrl={ctrl} disabled={editorDisabled} />

            <aside className="flex flex-col gap-3 rounded-md border border-border-soft bg-surface p-3.5">
              <header className="flex items-baseline justify-between gap-2">
                <h3 className="m-0 text-xs font-medium leading-4 tracking-letter-space text-muted-foreground">
                  {t('triage.refs.title')}
                </h3>
                <span className="font-mono text-micro tabular-nums text-faint-foreground">
                  {ctrl.totalSquads}
                </span>
              </header>

              {ctrl.totalSquads === 0 ? (
                <p className="m-0 text-micro leading-normal text-muted-foreground">
                  {t('triage.refs.empty')}
                </p>
              ) : (
                <div className="flex flex-col gap-3.5">
                  {ctrl.groups.map((group) => (
                    <div key={group.projectId} className="flex flex-col gap-1">
                      <div className="font-mono text-tiny font-medium uppercase tracking-eyebrow-wider text-faint-foreground">
                        {group.projectName}
                      </div>
                      {group.squads.map((squad) => {
                        const justInserted = ctrl.insertedCode === squad.code;
                        return (
                          <button
                            key={squad.id}
                            type="button"
                            onClick={() => {
                              ctrl.insertCode(squad.code);
                            }}
                            disabled={editorDisabled}
                            className="group flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:hover:bg-transparent"
                          >
                            <span className="min-w-0 flex-1 truncate text-meta text-foreground">
                              {squad.name}
                            </span>
                            <span
                              className={twcn(
                                'inline-flex items-center rounded-sm px-1.75 h-5 font-mono text-card-id font-medium',
                                squad.isUsed
                                  ? 'bg-tone-green-tint text-tone-green-ink'
                                  : 'bg-muted text-muted-foreground',
                              )}
                            >
                              {justInserted ? t('triage.refs.inserted') : squad.code}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  ))}
                </div>
              )}

              <p className="m-0 border-t border-border-soft pt-2.5 text-micro leading-normal text-faint-foreground">
                {t('triage.refs.footer')}
              </p>
            </aside>
          </div>
        </div>
      </Card>

      <Savebar
        open={ctrl.isDirty}
        disabled={ctrl.isSaving}
        onDiscard={ctrl.discard}
        onSave={ctrl.save}
      />
    </>
  );
}

/** The prompt column: label row (coverage, regenerate), the textarea and its hint. */
function PromptEditor({
  ctrl,
  disabled,
}: Readonly<{ ctrl: TriageController; disabled: boolean }>): React.JSX.Element {
  const { textareaRef } = ctrl;
  return (
    <div className="flex flex-col gap-1 lg:col-span-2">
      <div className="flex flex-wrap items-center gap-2">
        <Label
          htmlFor="triage-prompt"
          className="text-xs font-medium leading-4 tracking-letter-space text-muted-foreground"
        >
          {t('triage.prompt.label')}
        </Label>
        <span className="ml-auto font-mono text-micro tabular-nums text-muted-foreground">
          {t('triage.prompt.coverage', {
            used: ctrl.usedCount,
            total: ctrl.totalSquads,
          })}
        </span>
        <Button
          variant="outline"
          size="xs"
          onClick={ctrl.regenerate}
          disabled={disabled || ctrl.isRegenerating}
        >
          <AppIcons name="sparkle" size={12} />
          {ctrl.isRegenerating ? t('triage.prompt.regenerating') : t('triage.prompt.regenerate')}
        </Button>
      </div>
      <Textarea
        id="triage-prompt"
        ref={textareaRef}
        value={ctrl.prompt}
        onChange={(e) => {
          ctrl.setPrompt(e.target.value);
        }}
        disabled={disabled}
        rows={14}
        spellCheck={false}
        aria-label={t('triage.prompt.label')}
      />
      <p className="mt-1.5 text-micro leading-normal text-muted-foreground">
        {t('triage.prompt.hint')}
      </p>
    </div>
  );
}
